import { useState } from 'react'
import { Eye, MousePointerClick, Star, MessageSquare, RefreshCw, Loader2, Download } from 'lucide-react'
import ReactECharts from 'echarts-for-react'
import { useApi } from '../../hooks/useApi'
import { apiPost } from '../../lib/auth'

interface InsightsProps {
  locationId: number | null
}

interface KpiValue {
  value: number
  delta?: number
}

interface InsightsData {
  kpis: {
    totalViews: KpiValue
    totalActions: KpiValue
    avgRating: KpiValue
    reviewCount: KpiValue
  }
  timeseries: Array<Record<string, number | string>>
  channels: {
    desktop_search: number
    mobile_search: number
    desktop_maps: number
    mobile_maps: number
  }
}

type Period = '7d' | '30d' | '90d' | '12m' | 'all'

const PERIODS: { key: Period; label: string }[] = [
  { key: '7d', label: '7j' },
  { key: '30d', label: '30j' },
  { key: '90d', label: '90j' },
  { key: '12m', label: '12m' },
  { key: 'all', label: 'Tout' },
]

const KPI_CONFIG = [
  { key: 'totalViews' as const, label: 'Vues totales', icon: Eye, color: '#6366F1', bg: 'bg-[#6366F1]/10' },
  { key: 'totalActions' as const, label: 'Actions totales', icon: MousePointerClick, color: '#A855F7', bg: 'bg-[#A855F7]/10' },
  { key: 'avgRating' as const, label: 'Note moyenne', icon: Star, color: '#F59E0B', bg: 'bg-amber-400/10' },
  { key: 'reviewCount' as const, label: 'Nombre d\'avis', icon: MessageSquare, color: '#10B981', bg: 'bg-emerald-500/10' },
]

const CHANNEL_COLORS = {
  desktop_search: '#6366F1',
  mobile_search: '#A855F7',
  desktop_maps: '#10B981',
  mobile_maps: '#F59E0B',
}

const CHANNEL_LABELS: Record<string, string> = {
  desktop_search: 'Search Desktop',
  mobile_search: 'Search Mobile',
  desktop_maps: 'Maps Desktop',
  mobile_maps: 'Maps Mobile',
}

const TIMESERIES_KEYS = [
  { field: 'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH', label: 'Search Desktop', color: '#6366F1' },
  { field: 'BUSINESS_IMPRESSIONS_MOBILE_SEARCH', label: 'Search Mobile', color: '#A855F7' },
  { field: 'BUSINESS_IMPRESSIONS_DESKTOP_MAPS', label: 'Maps Desktop', color: '#10B981' },
  { field: 'BUSINESS_IMPRESSIONS_MOBILE_MAPS', label: 'Maps Mobile', color: '#F59E0B' },
]

function formatDelta(delta?: number) {
  if (delta === undefined || delta === null) return null
  const sign = delta >= 0 ? '+' : ''
  return `${sign}${delta.toFixed(1)}%`
}

function formatKpiValue(key: string, value: number) {
  if (key === 'avgRating') return value.toFixed(1)
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`
  return value.toLocaleString('fr-FR')
}

export function Insights({ locationId }: InsightsProps) {
  const [period, setPeriod] = useState<Period>('30d')
  const [syncing, setSyncing] = useState(false)
  const [backfilling, setBackfilling] = useState(false)

  const url = locationId ? `/api/local/locations/${locationId}/insights?period=${period}` : ''
  const { data, loading, error, refetch } = useApi<InsightsData>(url)

  const handleSync = async () => {
    if (!locationId || syncing) return
    setSyncing(true)
    try {
      await apiPost(`/api/local/locations/${locationId}/insights/sync`, {})
      refetch()
    } finally {
      setSyncing(false)
    }
  }

  const handleBackfill = async () => {
    if (!locationId || backfilling) return
    setBackfilling(true)
    try {
      await apiPost(`/api/local/locations/${locationId}/insights/backfill`, {})
      refetch()
    } finally {
      setBackfilling(false)
    }
  }

  if (!locationId) {
    return (
      <div className="text-center py-20 text-[#555A73] text-sm">
        S&eacute;lectionnez un &eacute;tablissement pour voir les insights.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#6366F1]" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-red-500 text-sm mb-2">Erreur : {error}</p>
        <button onClick={refetch} className="text-sm text-[#6366F1] hover:underline">
          R&eacute;essayer
        </button>
      </div>
    )
  }

  // No data yet — show backfill CTA
  if (!data || (!data.timeseries?.length && !data.kpis?.totalViews?.value)) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#555A73]">Aucune donn&eacute;e de performance disponible.</p>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] text-white text-sm font-medium rounded-xl hover:bg-[#5558E6] transition-colors disabled:opacity-60"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Synchroniser
          </button>
        </div>
        <div className="bg-white border border-[#E2E4ED] rounded-xl p-10 flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#6366F1]/10 flex items-center justify-center">
            <Download className="w-6 h-6 text-[#6366F1]" strokeWidth={1.25} />
          </div>
          <div className="text-center">
            <p className="text-[#1B2559] font-semibold mb-1">Importer l&rsquo;historique</p>
            <p className="text-[#555A73] text-sm max-w-sm">
              Importez jusqu&rsquo;&agrave; 18 mois de donn&eacute;es de performance GBP pour analyser les tendances.
            </p>
          </div>
          <button
            onClick={handleBackfill}
            disabled={backfilling}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#6366F1] text-white text-sm font-medium rounded-xl hover:bg-[#5558E6] transition-colors disabled:opacity-60"
          >
            {backfilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Importer l&rsquo;historique (18 mois)
          </button>
        </div>
      </div>
    )
  }

  // --- Line chart options ---
  const lineChartOption = {
    tooltip: {
      trigger: 'axis' as const,
      backgroundColor: '#1B2559',
      borderColor: '#2D3A6A',
      textStyle: { color: '#fff', fontSize: 12 },
    },
    legend: {
      bottom: 0,
      textStyle: { color: '#555A73', fontSize: 12 },
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
    },
    grid: { top: 16, right: 16, bottom: 40, left: 50 },
    xAxis: {
      type: 'category' as const,
      data: (data.timeseries || []).map((d) => {
        const dt = new Date(d.date as string)
        return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
      }),
      axisLine: { lineStyle: { color: '#E2E4ED' } },
      axisTick: { show: false },
      axisLabel: { color: '#8B8FA7', fontSize: 11 },
    },
    yAxis: {
      type: 'value' as const,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: '#F4F5FA' } },
      axisLabel: { color: '#8B8FA7', fontSize: 11 },
    },
    series: TIMESERIES_KEYS.map((tk) => ({
      name: tk.label,
      type: 'line' as const,
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 2, color: tk.color },
      itemStyle: { color: tk.color },
      data: (data.timeseries || []).map((d) => (d[tk.field] as number) ?? 0),
    })),
  }

  // --- Donut chart options ---
  const channelData = data.channels
    ? Object.entries(data.channels).map(([key, value]) => ({
        name: CHANNEL_LABELS[key] || key,
        value: value as number,
        itemStyle: { color: CHANNEL_COLORS[key as keyof typeof CHANNEL_COLORS] || '#8B8FA7' },
      }))
    : []

  const totalChannelViews = channelData.reduce((s, d) => s + d.value, 0)

  const donutOption = {
    tooltip: {
      trigger: 'item' as const,
      backgroundColor: '#1B2559',
      borderColor: '#2D3A6A',
      textStyle: { color: '#fff', fontSize: 12 },
      formatter: '{b}: {c} ({d}%)',
    },
    legend: { show: false },
    series: [
      {
        type: 'pie',
        radius: ['55%', '80%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          position: 'center' as const,
          formatter: () => `{val|${totalChannelViews.toLocaleString('fr-FR')}}\n{lab|vues totales}`,
          rich: {
            val: { fontSize: 22, fontWeight: 'bold' as const, color: '#1B2559', fontFamily: 'JetBrains Mono', lineHeight: 28 },
            lab: { fontSize: 11, color: '#8B8FA7', lineHeight: 16 },
          },
        },
        data: channelData,
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.1)' },
        },
      },
    ],
  }

  return (
    <div className="space-y-6">
      {/* Top bar: sync + period */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E2E4ED] text-sm font-medium text-[#1B2559] rounded-xl hover:border-[#C8CBD8] transition-colors disabled:opacity-60"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-[#555A73]" />}
          Synchroniser
        </button>

        <div className="flex items-center bg-[#F4F5FA] rounded-xl p-1 gap-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                period === p.key
                  ? 'bg-white text-[#1B2559] shadow-sm'
                  : 'text-[#8B8FA7] hover:text-[#555A73]'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {KPI_CONFIG.map((cfg) => {
          const Icon = cfg.icon
          const kpi = data.kpis?.[cfg.key]
          const value = kpi?.value ?? 0
          const delta = kpi?.delta

          return (
            <div
              key={cfg.key}
              className="bg-white border border-[#E2E4ED] rounded-xl p-5 flex flex-col gap-3"
            >
              <div className={`w-9 h-9 rounded-lg ${cfg.bg} flex items-center justify-center`}>
                <Icon className="w-[18px] h-[18px]" style={{ color: cfg.color }} strokeWidth={1.25} />
              </div>
              <div>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-mono font-bold text-[#1B2559]">
                    {formatKpiValue(cfg.key, value)}
                  </p>
                  {delta !== undefined && delta !== null && (
                    <span
                      className={`text-xs font-medium ${
                        delta >= 0 ? 'text-emerald-500' : 'text-red-500'
                      }`}
                    >
                      {formatDelta(delta)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#555A73] mt-0.5">{cfg.label}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Line chart */}
      <div className="bg-white border border-[#E2E4ED] rounded-xl p-6">
        <p className="text-sm font-semibold text-[#1B2559] mb-4">&Eacute;volution des vues par canal</p>
        <ReactECharts option={lineChartOption} style={{ height: 320 }} opts={{ renderer: 'svg' }} />
      </div>

      {/* Donut chart + legend */}
      <div className="bg-white border border-[#E2E4ED] rounded-xl p-6">
        <p className="text-sm font-semibold text-[#1B2559] mb-4">R&eacute;partition par canal</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <ReactECharts option={donutOption} style={{ height: 240 }} opts={{ renderer: 'svg' }} />
          <div className="space-y-3">
            {channelData.map((ch) => {
              const pct = totalChannelViews > 0 ? ((ch.value / totalChannelViews) * 100).toFixed(1) : '0'
              return (
                <div key={ch.name} className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: ch.itemStyle.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1B2559]">{ch.name}</p>
                  </div>
                  <p className="text-sm font-mono font-medium text-[#1B2559]">
                    {ch.value.toLocaleString('fr-FR')}
                  </p>
                  <p className="text-xs text-[#8B8FA7] w-12 text-right">{pct}%</p>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
